import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a distance given in metres, switching to kilometres once it reaches
 * 1 km. Returns the number and its unit separately so callers can style the
 * unit (e.g. a dimmed suffix).
 */
export function formatDistance(meters: number): {
  value: string;
  unit: string;
} {
  if (meters >= 1000) {
    return { value: (meters / 1000).toFixed(1), unit: 'km' };
  }
  return { value: String(Math.round(meters)), unit: 'm' };
}

/**
 * Format an offset in seconds as `00h19m` — hours and minutes, each zero
 * padded to two digits and carrying its own unit. Fixed width, so the three
 * timestamps on the block timeline's scale line up, and a match that crosses
 * the hour doesn't change shape.
 *
 * The backend's `match_summary.start_time` / `end_time` are frame indices
 * divided by the SOURCE fps, i.e. seconds into the recording — so this reads
 * as the same timestamp the player would scrub to. Minutes are floored rather
 * than rounded: a block's start should never point past where it begins.
 */
export function formatClock(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(minutes / 60))}h${pad(minutes % 60)}m`;
}

/** Convert a speed from metres-per-second to kilometres-per-hour. */
export const msToKmh = (ms: number): number => ms * 3.6;

/**
 * Format a speed given in metres-per-second as km/h, returning the number and
 * its unit separately (mirrors {@link formatDistance}) so callers can style the
 * unit suffix. The report surfaces average speed in km/h everywhere.
 */
export function formatSpeed(ms: number): { value: string; unit: string } {
  return { value: msToKmh(ms).toFixed(1), unit: 'km/h' };
}

/**
 * Format a speed already expressed in km/h. The segment `mean_velocity` field
 * is delivered in km/h (unlike cross-stats `mean_velocity_ms`), so it must NOT
 * be run through {@link msToKmh} — doing so inflates it ~3.6×.
 */
export function formatSpeedKmh(kmh: number): { value: string; unit: string } {
  return { value: kmh.toFixed(1), unit: 'km/h' };
}

/**
 * Allocate a set of values as whole percentages that always sum to exactly 100
 * (largest-remainder method). Use for "parts of a whole" legends so independent
 * per-slice rounding can't produce a 99%/101% total. Returns all-zeros when the
 * total is zero.
 */
export function largestRemainderPercents(values: number[]): number[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v / total) * 100);
  const out = exact.map(Math.floor);
  let remainder = 100 - out.reduce((sum, v) => sum + v, 0);
  // Hand the leftover whole percents to the largest fractional parts.
  const byFrac = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < byFrac.length && remainder > 0; k++) {
    out[byFrac[k].i] += 1;
    remainder--;
  }
  return out;
}

/**
 * The report token in the URL fragment (`#rt=<token>`), set by the Player
 * Dashboard when a signed-in player opens one of their own matches. A fragment
 * never reaches a server or its logs. Null when absent.
 */
export function reportTokenFromHash(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, '')).get('rt') || null;
}

/**
 * The report's look: `tieplayer` (the default, as the TiePlayer app opens it)
 * or `matchlytics` (black & white), asked for by the Player Dashboard in the
 * URL fragment (`#skin=matchlytics`). Anything else is the default.
 */
export type ReportSkin = 'tieplayer' | 'matchlytics';

export function reportSkinFromHash(hash: string): ReportSkin {
  const skin = new URLSearchParams(hash.replace(/^#/, '')).get('skin');
  return skin === 'matchlytics' ? 'matchlytics' : 'tieplayer';
}

/**
 * This page's skin, read once at load (the fragment doesn't change). main.tsx
 * puts it on <html> for the CSS (matchlytics-theme.css); the few colors drawn
 * from JS (chart fills, blended court marks) pick their palette from it.
 */
export const REPORT_SKIN: ReportSkin =
  typeof window === 'undefined' ? 'tieplayer' : reportSkinFromHash(window.location.hash);

/**
 * Whether "Desbloquear" has somewhere to go: only in the Matchlytics skin, framed
 * by the Player Dashboard, which sells the upgrade when asked (`requestUnlock`).
 * Elsewhere — the TiePlayer app, or the report opened on its own — it stays a label.
 */
export const CAN_UNLOCK =
  REPORT_SKIN === 'matchlytics' &&
  typeof window !== 'undefined' &&
  window.parent !== window;

/** Asks the dashboard framing the report to open its upgrade offer. Carries nothing but the ask. */
export const requestUnlock = () =>
  window.parent.postMessage({ type: 'matchlytics:unlock' }, '*');
